import { ExternalLink, FileText, Trash2, Upload } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import {
  ACEPTA,
  subirArchivo,
  validarArchivo,
  type DestinoSubida,
  type TipoArchivo,
} from '@/lib/archivos';

export interface ValorArchivo {
  path: string;
  url: string | null;
}

const esPdf = (path: string) => /\.pdf$/i.test(path);

/**
 * Un archivo (imagen o PDF) que se sube al CDN en cuanto se elige. El valor es la ruta
 * `src/content/...`; se guarda con el formulario que lo usa.
 */
export function ArchivoUnico({
  destino,
  ownerId,
  tipos,
  valor,
  onCambio,
  etiqueta = 'Archivo',
  quitable = false,
  deshabilitado = false,
}: {
  destino: DestinoSubida;
  ownerId?: number;
  tipos: TipoArchivo[];
  valor: ValorArchivo | null;
  onCambio: (v: ValorArchivo | null) => void;
  etiqueta?: string;
  quitable?: boolean;
  deshabilitado?: boolean;
}) {
  const [subiendo, setSubiendo] = useState(false);

  async function elegir(file: File | undefined) {
    if (!file) return;
    const error = validarArchivo(file, tipos);
    if (error) {
      toast.error(error);
      return;
    }
    setSubiendo(true);
    try {
      const r = await subirArchivo(file, destino, ownerId);
      onCambio({ path: r.path, url: r.url });
    } catch (e) {
      toast.error(mensajeDe(e));
    } finally {
      setSubiendo(false);
    }
  }

  return (
    <div>
      <span className="mb-1.5 block text-sm font-bold text-texto-2">{etiqueta}</span>
      <div className="flex flex-wrap items-center gap-3">
        {valor && !esPdf(valor.path) && valor.url ? (
          <a href={valor.url} target="_blank" rel="noreferrer">
            <img
              src={valor.url}
              alt={etiqueta}
              className="h-24 max-w-56 rounded-lg object-contain"
            />
          </a>
        ) : valor ? (
          <a
            href={valor.url ?? undefined}
            target="_blank"
            rel="noreferrer"
            className="flex h-24 w-40 flex-col items-center justify-center gap-1 rounded-lg border border-[var(--separador)] px-2 text-center text-xs text-texto-2"
          >
            <FileText size={22} aria-hidden />
            <span className="line-clamp-2 break-all">{valor.path.split('/').pop()}</span>
            {valor.url && <ExternalLink size={12} aria-hidden />}
          </a>
        ) : (
          <span className="grid h-24 w-40 place-items-center rounded-lg border border-dashed border-[var(--separador)] text-xs text-texto-3">
            Sin archivo
          </span>
        )}
        {!deshabilitado && (
          <div className="flex flex-col gap-2">
            <label className="btn-cs btn-vidrio cursor-pointer">
              <Upload size={16} aria-hidden />{' '}
              {subiendo ? 'Subiendo…' : valor ? 'Reemplazar' : 'Elegir archivo'}
              <input
                type="file"
                className="sr-only"
                accept={tipos.map((t) => ACEPTA[t]).join(',')}
                disabled={subiendo}
                onChange={(e) => {
                  void elegir(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
            {quitable && valor && (
              <button type="button" className="btn-cs btn-vidrio" onClick={() => onCambio(null)}>
                <Trash2 size={16} aria-hidden /> Quitar
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
